import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { ContextApiService } from '@core/context/services/context-api.service';
import { ContextoPWA } from '@shared/types';
import { STORAGE_KEYS, LEGACY_STORAGE_KEYS } from '@shared/constants';
import {
  AcompanhamentoAluno,
  AcompanhamentoComponenteCurricular,
  AcompanhamentoFiltros,
  AcompanhamentoFormulario,
  AcompanhamentoPedagogicoRegistro,
  AcompanhamentoPreferencias,
  AcompanhamentoPeriodo
} from '../models/acompanhamento-pedagogico.types';
import {
  AcompanhamentoApiItemEducacional,
  AcompanhamentoApiRegistro,
  AcompanhamentoApiTurma
} from '../models/acompanhamento-pedagogico.api.types';
import { AcompanhamentoPedagogicoMapper } from '../adapters/acompanhamento-pedagogico.mapper';
import { AcompanhamentoPedagogicoApiService } from './acompanhamento-pedagogico-api.service';
import { AcompanhamentoPedagogicoPreferencesService } from './acompanhamento-pedagogico-preferences.service';

@Injectable({
  providedIn: 'root'
})
export class AcompanhamentoPedagogicoService {
  private itensTurmaCache = new Map<number, AcompanhamentoApiItemEducacional[]>();
  private registrosCache = new Map<number, AcompanhamentoApiRegistro>();
  private componentesCache = new Map<number, AcompanhamentoComponenteCurricular[]>();

  constructor(
    private api: AcompanhamentoPedagogicoApiService,
    private contextApi: ContextApiService,
    private preferences: AcompanhamentoPedagogicoPreferencesService
  ) {}

  getPeriodos(contexto: ContextoPWA | null): Observable<AcompanhamentoPeriodo[]> {
    const turmaId = contexto?.turma?.id;
    if (!turmaId) {
      return of([]);
    }

    const hojeIso = this.getHojeIso();

    return forkJoin({
      periodos: this.api.getPeriodos(turmaId).pipe(catchError(() => of([]))),
      encerramentos: this.api.getEncerramentos(turmaId).pipe(catchError(() => of([])))
    }).pipe(
      map(({ periodos, encerramentos }) => {
        const periodosArray = Array.isArray(periodos) ? periodos : [];
        return periodosArray.map((periodo) => {
          const encerramento = encerramentos.find((enc) => {
            if (enc.periodoAvaliativo?.id === periodo.id) return true;
            if (enc.tipoPeriodo === 'EXAME_FINAL' && enc.identificadorExameFinal &&
                parseInt(enc.identificadorExameFinal) === periodo.id) return true;
            return false;
          });
          const encerrado = encerramento ? encerramento.status === 'ENCERRADO' : !!periodo.encerrado;
          return AcompanhamentoPedagogicoMapper.toPeriodo({ ...periodo, encerrado }, hojeIso);
        });
      }),
      map((periodos) => {
        if (periodos.some((periodo) => periodo.ativo)) {
          return periodos;
        }

        if (periodos[0]) {
          periodos[0].ativo = true;
        }

        return periodos;
      }),
      switchMap((periodos) => {
        if (periodos.length > 0) {
          return of(periodos);
        }
        // Verifica primeiro pelo contexto (bridge) pra evitar custo de API
        const eAtividadeNoContexto = this.isTurmaAtividadeOuModularFromContexto(contexto);
        if (eAtividadeNoContexto) {
          return this.getPeriodoPorOrganizacaoCurricular(contexto, hojeIso);
        }
        return this.api.getTurma(turmaId!).pipe(
          switchMap((turma) => {
            if (this.isTurmaAtividadeOuModular(turma)) {
              return this.getPeriodoPorOrganizacaoCurricular(contexto, hojeIso);
            }
            return of([]);
          }),
          catchError(() => of([]))
        );
      }),
      catchError(() => of([]))
    );
  }

  private isTurmaAtividadeOuModular(turma: AcompanhamentoApiTurma): boolean {
    const tiposAtividade = ['ATIVIDADE_COMPLEMENTAR', 'ATENDIMENTO_EDUCACIONAL_ESPECIALIZADO'];
    return tiposAtividade.includes(String(turma.tipoAtendimento || '')) || Boolean(turma.etapaMatriz?.modular);
  }

  // Versão usada quando a turma completa não foi buscada via API (ex: filtro de alunos elegíveis).
  private isTurmaAtividadeOuModularFromContexto(contexto: ContextoPWA | null): boolean {
    const turma = (contexto?.turma || {}) as { tipoAtendimento?: string; etapaMatriz?: { modular?: boolean } };
    const tiposAtividade = ['ATIVIDADE_COMPLEMENTAR', 'ATENDIMENTO_EDUCACIONAL_ESPECIALIZADO'];
    return tiposAtividade.includes(String(turma.tipoAtendimento || '')) || Boolean(turma.etapaMatriz?.modular);
  }

  private getPeriodoPorOrganizacaoCurricular(
    contexto: ContextoPWA | null,
    hojeIso: string
  ): Observable<AcompanhamentoPeriodo[]> {
    const turmaId = contexto?.turma?.id;
    const primeiroComponenteId = contexto?.componentes?.[0]?.id;

    // Quando contexto.componentes não veio (bridge só passou turmaId), busca via
    // /context/turma/{id}/itens-educacionais — mesma fonte usada pela frequência.
    const componenteId$: Observable<number | null> = primeiroComponenteId
      ? of(primeiroComponenteId)
      : turmaId
        ? this.contextApi.getComponentesCurriculares(turmaId, this.getParametrosContexto(contexto)).pipe(

            map((itens) => itens[0]?.id ?? null),
            catchError(() => of(null))
          )
        : of(null);

    return componenteId$.pipe(
      switchMap((componenteId) => {
        if (!componenteId) {
          return of([]);
        }
        return this.api.getItemEducacionalDetalhado(componenteId).pipe(
          map((item) => {
            if (!item.dataInicio || !item.dataFim) {
              return [];
            }
            const periodo: AcompanhamentoPeriodo = {
              id: 0,
              descricao: item.descricao || item.nome || '1º período',
              dataInicial: item.dataInicio,
              dataFinal: item.dataFim,
              ativo: true,
              encerrado: false
            };
            return [periodo];
          }),
          catchError(() => of([]))
        );
      })
    );
  }

  getComponentes(contexto: ContextoPWA | null): Observable<AcompanhamentoComponenteCurricular[]> {
    const turmaId = contexto?.turma?.id;
    if (typeof turmaId !== 'number') {
      return of([]);
    }
    const turmaIdResolvido = turmaId;
    const componentesContexto = this.getComponentesContexto(contexto);

    return this.getComponentesCurricularesContexto(turmaIdResolvido, contexto).pipe(
      map((componentes) => this.filtrarComponentesSelecionados(contexto, componentes.length ? componentes : componentesContexto)),
      catchError(() =>
        this.getComponentesCurricularesLegado(turmaIdResolvido, componentesContexto).pipe(
          map((componentes) => this.filtrarComponentesSelecionados(contexto, componentes))
        )
      ),
      tap((componentes) => this.componentesCache.set(turmaIdResolvido, componentes))
    );
  }

  private getComponentesCurricularesContexto(
    turmaId: number,
    contexto: ContextoPWA | null
  ): Observable<AcompanhamentoComponenteCurricular[]> {
    return forkJoin({
      componentesApi: this.contextApi.getComponentesCurriculares(turmaId, this.getParametrosContexto(contexto)),
      itensTurma: this.api.getItensTurma(turmaId).pipe(
        tap((itens) => this.itensTurmaCache.set(turmaId, itens)),
        catchError(() => of([] as AcompanhamentoApiItemEducacional[]))
      )
    }).pipe(
      map(({ componentesApi, itensTurma }) =>
        componentesApi.map((componente) => {
          const itemFilho = itensTurma.find((it) => it.id === componente.id);
          return {
            id: componente.id,
            descricao: componente.nome || componente.descricao || 'Componente curricular',
            itemEducacionalId: itemFilho?.id || componente.id,
            etapaMatrizDisciplinaId: itemFilho?.etapaMatrizDisciplina?.id || null,
            ativo: true
          };
        })
      )
    );
  }

  private getComponentesCurricularesLegado(
    turmaId: number,
    componentesContexto: AcompanhamentoComponenteCurricular[]
  ): Observable<AcompanhamentoComponenteCurricular[]> {
    return this.api.getItensTurma(turmaId).pipe(
      tap((itens) => this.itensTurmaCache.set(turmaId, itens)),
      map((itens) => this.normalizarItensTurma(itens)),
      map((itens) => itens.map((item) => AcompanhamentoPedagogicoMapper.toComponente(item))),
      map((componentesApi) => componentesApi.length ? componentesApi : componentesContexto),
      catchError(() => of(componentesContexto))
    );
  }

  getAlunosElegiveis(
    contexto: ContextoPWA | null,
    dataRegistro: string,
    componenteId: number | null,
    filtrarPublicoAlvo: boolean
  ): Observable<AcompanhamentoAluno[]> {
    const turmaId = contexto?.turma?.id;
    const estabelecimentoId = contexto?.estabelecimento?.id;
    
    if (typeof turmaId !== 'number' || typeof estabelecimentoId !== 'number') {
      return of([]);
    }
    const turmaIdResolvido = turmaId;
    const estabelecimentoIdResolvido = estabelecimentoId;
    const componenteSelecionado = this.getComponenteContexto(contexto, componenteId);
    const isTurmaAtividadeOuModular = this.isTurmaAtividadeOuModularFromContexto(contexto);
    const isTurmaModular = Boolean((contexto?.turma as any)?.etapaMatriz?.modular);

    return forkJoin({
      itensTurma: this.getItensTurmaCached(turmaIdResolvido),
      enturmacoes: this.api.getEnturmacoesElegiveis({
        turmaId: turmaIdResolvido,
        estabelecimentoId: estabelecimentoIdResolvido,
        dataRegistro,
        etapaMatrizDisciplinaId: isTurmaModular ? (componenteSelecionado?.etapaMatrizDisciplinaId || null) : null,
        itemEducacionalId: isTurmaAtividadeOuModular ? (componenteSelecionado?.itemEducacionalId ?? null) : null,
        filtrarPublicoAlvo
      })
    }).pipe(
      map(({ itensTurma, enturmacoes }) =>
        enturmacoes.map((enturmacao) => AcompanhamentoPedagogicoMapper.toAluno(enturmacao, itensTurma))
      ),
      map((alunos) => isTurmaAtividadeOuModular ? this.filtrarAlunosElegiveisPorComponente(alunos, componenteId) : alunos)
    );
  }

  private filtrarAlunosElegiveisPorComponente(
    alunos: AcompanhamentoAluno[],
    componenteId: number | null
  ): AcompanhamentoAluno[] {
    if (!componenteId) {
      return alunos;
    }
    return alunos.filter((aluno) => {

      if (!aluno.disciplinas.length) {
        return true;
      }
      const vinculo = aluno.disciplinas.find((disciplina) => disciplina.componenteId === componenteId);
      return !!vinculo && !!vinculo.ativo && !vinculo.dispensado;
    });
  }

  getRegistros(contexto: ContextoPWA | null, periodoId?: number, filtros?: AcompanhamentoFiltros): Observable<AcompanhamentoPedagogicoRegistro[]> {
    return this.getRegistrosRemotos(contexto, periodoId || 0, filtros || {});
  }

  savePreferencias(preferencias: AcompanhamentoPreferencias): void {
    this.preferences.save(preferencias);
  }

  loadPreferencias(): AcompanhamentoPreferencias {
    return this.preferences.load();
  }

  salvarRegistro(
    contexto: ContextoPWA | null,
    formulario: AcompanhamentoFormulario,
    periodoId: number,
    componentes: AcompanhamentoComponenteCurricular[],
    alunos: AcompanhamentoAluno[],
    campoAdicional?: Record<string, unknown> | null
  ): Observable<AcompanhamentoPedagogicoRegistro[]> {
    const turmaId = contexto?.turma?.id;
    const estabelecimentoId = contexto?.estabelecimento?.id;

    if (!turmaId || !estabelecimentoId) {
      return of([]);
    }

    const payload = AcompanhamentoPedagogicoMapper.toSavePayload({
      formulario,
      turmaId,
      estabelecimentoId,
      componentes,
      alunos,
      campoAdicional,
      registroAtual: formulario.id ? this.registrosCache.get(formulario.id) : undefined
    });

    const request$ = formulario.id
      ? this.api.update(payload)
      : this.api.createMany(payload);

    return request$.pipe(switchMap(() => this.getRegistros(contexto, periodoId)));
  }

  excluirRegistro(contexto: ContextoPWA | null, registroId: number, periodoId?: number): Observable<AcompanhamentoPedagogicoRegistro[]> {
    return this.api.delete(registroId).pipe(
      tap(() => this.registrosCache.delete(registroId)),
      switchMap(() => this.getRegistros(contexto, periodoId))
    );
  }

  atualizarPublicacao(
    contexto: ContextoPWA | null,
    registroId: number,
    publicado: boolean,
    periodoId?: number
  ): Observable<AcompanhamentoPedagogicoRegistro[]> {
    const registro = this.registrosCache.get(registroId);
    if (registro) {
      registro.publicar = publicado;
      if (!publicado) {
        delete registro.dataPublicacao;
      }
    }

    return this.api.publishToggle(registroId).pipe(
      switchMap(() => this.getRegistros(contexto, periodoId))
    );
  }

  private getRegistrosRemotos(contexto: ContextoPWA | null, periodoId: number, filtros: AcompanhamentoFiltros): Observable<AcompanhamentoPedagogicoRegistro[]> {
    const turmaId = contexto?.turma?.id;
    if (!turmaId) {
      return of([]);
    }
    const turmaIdResolvido = turmaId;
    const termoBusca = this.normalizarBusca(filtros.termo);

    return this.getPeriodos(contexto).pipe(
      switchMap((periodos) =>
        this.api.list({
          turmaId: turmaIdResolvido,
          data: filtros.data
        })
          .pipe(map((response) => ({ response, periodos })))
      ),
      map(({ response, periodos }) =>
        (response.content || [])
          .map((registro) => ({
            registro,
            dataRegistro: this.getDataRegistro(registro),
            periodo:
              periodos.find((periodoAtual) => {
                const dataRegistro = this.getDataRegistro(registro);
                return !!dataRegistro && dataRegistro >= periodoAtual.dataInicial && dataRegistro <= periodoAtual.dataFinal;
              }) || null
          }))
          .filter(({ dataRegistro, periodo }) => {
            if (!dataRegistro) {
              return false;
            }

            if (!periodoId) {
              return true;
            }

            return periodo?.id === periodoId || !periodo;
          })
      ),
      tap((registros) => {
        this.registrosCache.clear();
        registros.forEach(({ registro }) => {
          if (registro.id) {
            this.registrosCache.set(registro.id, registro);
          }
        });
      }),
      map((registros) =>
        registros.map(({ registro, periodo }) => ({
          ...AcompanhamentoPedagogicoMapper.toRegistro(registro),
          periodoId: periodo?.id || periodoId || 0
        }))
      ),
      switchMap((registros) => this.enrichRegistrosComNomesSociais(contexto, registros)),
      map((registros) => registros.filter((registro) => this.correspondeBuscaRegistro(registro, termoBusca)))
    );
  }

  private enrichRegistrosComNomesSociais(
    contexto: ContextoPWA | null,
    registros: AcompanhamentoPedagogicoRegistro[]
  ): Observable<AcompanhamentoPedagogicoRegistro[]> {
    const turmaId = contexto?.turma?.id;
    const estabelecimentoId = contexto?.estabelecimento?.id;

    if (!turmaId || !estabelecimentoId || !registros.length) {
      return of(registros);
    }

    const isTurmaModular = this.isTurmaAtividadeOuModularFromContexto(contexto);
    const hojeIso = this.getHojeIso();

    // Não-modulares: 1 chamada sem itemEducacionalId retorna todas as enturmações.
    // Modulares: 1 chamada por componenteId (que é o itemEducacionalId).
    const itemEducacionalIds: (number | null)[] = isTurmaModular
      ? [...new Set(registros.map((r) => r.componenteId).filter((id) => id > 0))]
      : [null];

    const calls$ = itemEducacionalIds.map((itemEducacionalId) =>
      this.api.getEnturmacoesElegiveis({
        turmaId: turmaId!,
        estabelecimentoId: estabelecimentoId!,
        dataRegistro: hojeIso,
        etapaMatrizDisciplinaId: null,
        itemEducacionalId,
        filtrarPublicoAlvo: false
      }).pipe(catchError(() => of([])))
    );

    return forkJoin(calls$).pipe(
      map((results) => {
        const socialMap = new Map<number, string | null>();
        results.flat().forEach((enturmacao) => {
          if (enturmacao.id) {
            socialMap.set(enturmacao.id, enturmacao.matricula?.nomeFantasia || null);
          }
        });

        return registros.map((registro) => ({
          ...registro,
          alunoNomeSocial: socialMap.get(registro.alunoId) ?? registro.alunoNomeSocial ?? null
        }));
      })
    );
  }

  private correspondeBuscaRegistro(
    registro: AcompanhamentoPedagogicoRegistro,
    termoBusca: string
  ): boolean {
    if (!termoBusca) {
      return true;
    }

    const camposBusca = [
      registro.alunoNomeSocial || '',
      registro.alunoNome,
      registro.observacao
    ].map((valor) => this.normalizarBusca(valor));

    return camposBusca.some((valor) => valor.includes(termoBusca));
  }

  private normalizarBusca(value?: string | null): string {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase()
      .trim();
  }

  private getItensTurmaCached(turmaId: number): Observable<AcompanhamentoApiItemEducacional[]> {
    const cached = this.itensTurmaCache.get(turmaId);
    if (cached) {
      return of(cached);
    }


    return this.api.getItensTurma(turmaId).pipe(
      tap((itens) => this.itensTurmaCache.set(turmaId, itens))
    );
  }

  private getDataRegistro(registro: AcompanhamentoApiRegistro): string {
    return registro.dataRegistro || registro.data || '';
  }

  private normalizarItensTurma(itens: AcompanhamentoApiItemEducacional[]): AcompanhamentoApiItemEducacional[] {
    const deduplicados = new Map<number, AcompanhamentoApiItemEducacional>();
    itens.forEach((item) => {
      const id = item.pai?.id || item.id;
      if (!deduplicados.has(id)) {
        deduplicados.set(id, item.pai ? { ...item.pai, id } : item);
      }
    });

    return Array.from(deduplicados.values());
  }

  private getHojeIso(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private filtrarComponentesSelecionados(
    contexto: ContextoPWA | null,
    componentes: AcompanhamentoComponenteCurricular[]
  ): AcompanhamentoComponenteCurricular[] {
    const componentesContexto = this.getComponentesContexto(contexto);

    if (!componentesContexto.length) {
      return componentes;
    }

    const componentesPorId = new Map<number, AcompanhamentoComponenteCurricular>();
    componentes.forEach((componente) => {
      componentesPorId.set(componente.id, componente);
    });

    return componentesContexto.map((componenteContexto) => {
      const componenteResolvido = componentesPorId.get(componenteContexto.id);
      return componenteResolvido
        ? { ...componenteContexto, ...componenteResolvido }
        : componenteContexto;
    });
  }

  private getComponenteContexto(
    contexto: ContextoPWA | null,
    componenteId: number | null
  ): AcompanhamentoComponenteCurricular | null {
    if (!componenteId) {
      return null;
    }

    const componentesContexto = this.getComponentesContexto(contexto);
    const turmaId = contexto?.turma?.id;
    const componentesCache = typeof turmaId === 'number' ? this.componentesCache.get(turmaId) || [] : [];

    return (
      componentesCache.find((item) => item.id === componenteId) ||
      componentesContexto.find((item) => item.id === componenteId) ||
      null
    );
  }

  private getComponentesContexto(contexto: ContextoPWA | null): AcompanhamentoComponenteCurricular[] {
    return (contexto?.componentes || []).map((item) => ({
      id: item.id,
      descricao: item.nome || item.descricao || 'Componente curricular',
      itemEducacionalId: item.id,
      etapaMatrizDisciplinaId: null,
      ativo: true
    }));
  }

  private getParametrosContexto(contexto: ContextoPWA | null): {
    userAccessId?: string;
    servidorId?: number;
    anoLetivoId?: number;
    estabelecimentoId?: number;
  } {
    const userAccessId = this.getUserAccessId();

    return {
      userAccessId: userAccessId || undefined,
      servidorId: contexto?.servidor?.id,
      anoLetivoId: contexto?.anoLetivo?.id,
      estabelecimentoId: contexto?.estabelecimento?.id
    };
  }

  private getUserAccessId(): string {
    try {
      // Fallback de leitura na chave antiga (__pwa_bridge_user_access) por 1 release — TODO remover
      return localStorage.getItem(STORAGE_KEYS.BRIDGE_USER_ACCESS) ||
        localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_USER_ACCESS) || '';
    } catch {
      return '';
    }
  }
}
